import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import type { GroupRosterItem } from 'hooks/incident/useRoster';
import { DhsResourceEditForm } from './DhsResourceEditForm';

export type AssetFormLayout = 'horizontal' | 'vertical';

const PRESSED =
  'tw:data-[state=on]:!bg-primary tw:data-[state=on]:!text-primary-foreground tw:aria-pressed:!bg-primary tw:aria-pressed:!text-primary-foreground';

export function AssetFormLayoutToggle({
  layout,
  onLayoutChange,
}: {
  layout: AssetFormLayout;
  onLayoutChange: (layout: AssetFormLayout) => void;
}) {
  return (
    <ToggleGroup
      variant="outline"
      size="sm"
      spacing={0}
      multiple={false}
      value={[layout]}
      onValueChange={(next) => {
        const value = Array.isArray(next) ? next[0] : next;
        if (value === 'horizontal' || value === 'vertical') {
          onLayoutChange(value);
        }
      }}
      aria-label="Form layout"
    >
      <ToggleGroupItem value="vertical" className={PRESSED}>
        1 column
      </ToggleGroupItem>
      <ToggleGroupItem value="horizontal" className={PRESSED}>
        2 columns
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

export function DhsResourceAddDialog({
  open,
  onOpenChange,
  groupMembers,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupMembers: GroupRosterItem[];
}) {
  const [layout, setLayout] = useState<AssetFormLayout>('vertical');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'dark tw:max-h-[85vh] tw:overflow-y-auto tw:bg-background tw:text-foreground',
          layout === 'horizontal' ? 'tw:!max-w-3xl' : 'tw:!max-w-lg'
        )}
      >
        <DialogHeader className="tw:!w-full tw:!flex-row tw:items-center tw:justify-between tw:pr-8">
          <DialogTitle>Add Asset</DialogTitle>
          <AssetFormLayoutToggle layout={layout} onLayoutChange={setLayout} />
        </DialogHeader>
        <DhsResourceEditForm
          groupMembers={groupMembers}
          layout={layout}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
